import type {
  BUTTON_TYPE_CATALOG,
  BUTTON_TYPE_FLOW,
  BUTTON_TYPE_MPM,
  BUTTON_TYPE_REQUEST_CONTACT_INFO,
  BUTTON_TYPE_SPM,
  HEADER_FORMATS,
} from "@dashboardApi/apis/whatsappMessageTemplates/constants";

interface WhatsAppTemplateQuickReplyButton {
  type: "QUICK_REPLY";
  text: string;
}

interface WhatsAppTemplateUrlButton {
  type: "URL";
  url: string;
  text: string;
}

interface WhatsAppTemplatePhoneNumberButton {
  type: "PHONE_NUMBER";

  phone_number: string;
  text: string;
}

interface WhatsAppTemplateCopyCodeButton {
  type: "COPY_CODE";
  text: string;
  offer_code: string;
}

export type WhatsAppPaymentSettingType =
  | "boleto"
  | "pix_dynamic_code"
  | "payment_link"
  | "BOLETO"
  | "PIX_DYNAMIC_CODE"
  | "PAYMENT_LINK";

interface WhatsAppTemplatePaymentRequestButton {
  type: "PAYMENT_REQUEST";
  text: string;
  payment_setting: {
    type: WhatsAppPaymentSettingType;
    boleto?: {
      digitable_line?: string;
    };
    pix_dynamic_code?: {
      code?: string;
    };
    payment_link?: {
      uri?: string;
    };
  };
}

interface WhatsAppTemplateCatalogButton {
  type: typeof BUTTON_TYPE_CATALOG;
  text: string;
}

interface WhatsAppTemplateMultiProductButton {
  type: typeof BUTTON_TYPE_MPM;
  text: string;
}

interface WhatsAppTemplateSingleProductButton {
  type: typeof BUTTON_TYPE_SPM;
  text: string;
}

interface WhatsAppTemplateFlowButton {
  type: typeof BUTTON_TYPE_FLOW;
  text: string;
  // Note: This reflects the field as returned by the Meta Templates API
  // We convert this to a string when mapping the Meta response onto our own types (EditableButton), since flow ID is supposed to be a string
  flow_id: number;
  // Undefined if template uses the default icon
  icon?: WhatsAppIconType;
}

interface WhatsAppTemplateRequestContactInfoButton {
  type: typeof BUTTON_TYPE_REQUEST_CONTACT_INFO;
  // Meta fixes this label and localizes it per recipient, so the GET shape may omit it
  text?: string;
}

// If updating, also update hasOnlySupportedButtons
type WhatsAppTemplateButton =
  | WhatsAppTemplateQuickReplyButton
  | WhatsAppTemplateUrlButton
  | WhatsAppTemplatePhoneNumberButton
  | WhatsAppTemplateCopyCodeButton
  | WhatsAppTemplatePaymentRequestButton
  | WhatsAppTemplateCatalogButton
  | WhatsAppTemplateMultiProductButton
  | WhatsAppTemplateSingleProductButton
  | WhatsAppTemplateFlowButton
  | WhatsAppTemplateRequestContactInfoButton;

// WhatsApp Flow templates can have a custom icon configured in the WhatsApp Manager
export type WhatsAppIconType = "REVIEW" | "PROMOTION" | "DOCUMENT";

export type WhatsAppButtonType =
  | "QUICK_REPLY"
  | "URL"
  | "PHONE_NUMBER"
  | "COPY_CODE"
  | "PAYMENT_REQUEST"
  | "LIST"
  | typeof BUTTON_TYPE_CATALOG
  | typeof BUTTON_TYPE_MPM
  | typeof BUTTON_TYPE_SPM
  | typeof BUTTON_TYPE_FLOW
  | typeof BUTTON_TYPE_REQUEST_CONTACT_INFO
  | "UNTYPED";

export interface HeaderComponent {
  type: "HEADER";
  text?: string;
  format:
    | typeof HEADER_FORMATS.TEXT
    | typeof HEADER_FORMATS.IMAGE
    | typeof HEADER_FORMATS.VIDEO
    | typeof HEADER_FORMATS.DOCUMENT
    | typeof HEADER_FORMATS.LOCATION;
}

export interface TextComponent {
  type: "BODY" | "FOOTER";
  text: string;
}

export interface ButtonsComponent {
  type: "BUTTONS";
  buttons: WhatsAppTemplateButton[];
}

export interface CarouselCard {
  components: Exclude<WhatsAppMessageTemplateComponent, CarouselComponent>[];
}

export interface CarouselComponent {
  type: "CAROUSEL";
  cards: CarouselCard[];
}

export interface LimitedTimeOfferComponent {
  type: "LIMITED_TIME_OFFER";
  limited_time_offer: {
    text: string;
    has_expiration: boolean;
  };
}

export type WhatsAppMessageTemplateComponent =
  | TextComponent
  | ButtonsComponent
  | HeaderComponent
  | CarouselComponent
  | LimitedTimeOfferComponent;

export function isButtonsComponent(c: WhatsAppMessageTemplateComponent): c is ButtonsComponent {
  return Array.isArray((c as ButtonsComponent).buttons);
}

export function isCarouselComponent(c: WhatsAppMessageTemplateComponent): c is CarouselComponent {
  return c.type === "CAROUSEL" && c.cards.length > 0;
}

export interface WhatsAppMessageTemplate {
  name: string;
  components: WhatsAppMessageTemplateComponent[];
  language: string;
  status: string;
  category: string;
  id: string;
  is_draft?: boolean;
  /**
   * Meta Graph `rejected_reason` enum when status is REJECTED.
   * Null/absent when Meta does not provide one (e.g. scheduled for deletion).
   */
  rejected_reason?: string | null;

  last_updated_time?: string;
}
